import type { FontSubset } from './types';

export type FontSubsetCacheEntry = Pick<FontSubset, 'fontFamily' | 'chars' | 'name'> & {
    /**
     * 字体子集文件字节
     */
    bytes: Uint8Array;
};

/**
 * 子集字节缓存（cache）的 LRU 容量上限
 */
const SUBSET_CACHE_MAX_SIZE = 256;

/**
 * 子集字节缓存（LRU）。
 *
 * 背景：cache 以 registerTarget + subsetName 为键缓存已拉取的子集字节，
 * 用于"同一 registerTarget 的同一 subsetName 再次出现时免重新拉取"（典型如撤销/重做恢复图层、同字体同文案重复插入）。
 * engine 使用 TTF；document 优先使用 WOFF2、缺失时回退 TTF，因此两个目标不得共享同名子集字节。
 *
 * 按图层维护后，一次长会话会陆续 fork 出大量不同 subsetName，若 cache 只增不减会持续占用内存；
 * 因此对 cache 设 LRU 上限：超过上限时淘汰最久未使用的字节条目，
 * 自然保留最近/高频复现的子集，把字节缓存占用限制在常数级。
 *
 * 容量由 {@link SUBSET_CACHE_MAX_SIZE} 约束：利用 Map 的插入顺序表达"使用近度"——
 * 越靠前越久未使用，命中刷新到末尾，超量时从最前面淘汰最久未使用条目，使内存占用保持常数级。
 *
 * 注意：cache 字节与引擎注册生命周期无关。即使淘汰了已注册子集的缓存条目也安全，
 * 引擎仍持有字体数据，渲染不受影响。
 */
export class FontSubsetCache {
    private entries = new Map<string, FontSubsetCacheEntry>();

    private maxSize = SUBSET_CACHE_MAX_SIZE;

    /**
     * 读取缓存条目，并把命中项标记为最近使用（移到末尾）。
     *
     * @param key - 注册目标 + 子集名称
     * @returns 缓存条目，未命中时返回 undefined
     */
    public get(key: string): FontSubsetCacheEntry | undefined {
        const entry = this.entries.get(key);
        if (!entry) return undefined;

        // 命中即刷新近度：移到 Map 末尾。
        this.entries.delete(key);
        this.entries.set(key, entry);
        return entry;
    }

    /**
     * 写入缓存条目（置于末尾），并在超过容量上限时淘汰最久未使用的条目。
     *
     * @param key - 注册目标 + 子集名称
     * @param entry - 缓存条目
     */
    public set(key: string, entry: FontSubsetCacheEntry) {
        // 覆盖写时先删再插，确保其位于 Map 末尾（最近使用）。
        this.entries.delete(key);
        this.entries.set(key, entry);

        while (this.entries.size > this.maxSize) {
            const oldest = this.entries.keys().next().value;
            if (oldest === undefined) break;
            this.entries.delete(oldest);
        }
    }

    /**
     * 清空全部缓存条目（卸载时释放字节）。
     */
    public clear() {
        this.entries.clear();
    }
}
